import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, ExternalLink, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { fetchPosts, adminDeletePost, formatApiError } from "../../lib/api";

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const items = await fetchPosts({ limit: 500 });
      setPosts(items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => posts.filter((p) => !q || (p.title + p.slug + p.kicker).toLowerCase().includes(q.toLowerCase())),
    [posts, q]
  );

  const onDelete = async (slug, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await adminDeletePost(slug);
      toast.success("Post deleted");
      setPosts((ps) => ps.filter((p) => p.slug !== slug));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-posts-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-10">
        <div>
          <p className="kicker text-stone-500">Dashboard</p>
          <h1 className="font-serif text-4xl font-light mt-2 text-stone-900">Posts</h1>
          <p className="text-stone-600 text-sm mt-1">{posts.length} stories in the archive.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center border-b border-stone-300 focus-within:border-[#9c4122] w-64">
            <Search size={14} className="text-stone-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search posts"
              data-testid="admin-posts-search"
              className="flex-1 bg-transparent outline-none px-2 py-1.5 text-sm"
            />
          </div>
          <Link
            to="/admin/posts/new"
            data-testid="admin-new-post-button"
            className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 text-[12px] tracking-[0.18em] uppercase font-semibold hover:bg-[#9c4122] transition-colors"
          >
            <Plus size={14} /> New Post
          </Link>
        </div>
      </div>

      {loading ? (
        <p className="text-stone-500 text-sm">Loading…</p>
      ) : (
        <div className="bg-white border border-stone-200">
          <table className="w-full">
            <thead className="border-b border-stone-200">
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-stone-500">
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3 hidden md:table-cell">Categories</th>
                <th className="px-5 py-3 hidden sm:table-cell">Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-16 text-center text-stone-500">No posts found.</td></tr>
              ) : filtered.map((p) => (
                <tr key={p.slug} data-testid={`admin-post-row-${p.slug}`} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <img src={p.hero} alt="" className="w-14 h-14 object-cover bg-stone-200 hidden sm:block" loading="lazy" />
                      <div>
                        <p className="font-serif text-lg leading-tight text-stone-900">{p.title}</p>
                        <p className="text-xs text-stone-500 mt-1">/{p.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell text-sm text-stone-600">
                    {(p.categories || []).slice(0, 3).join(", ")}
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell text-sm text-stone-600">{p.date}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 justify-end">
                      <a href={`/post/${p.slug}`} target="_blank" rel="noreferrer" title="View"
                         data-testid={`admin-view-${p.slug}`}
                         className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100">
                        <ExternalLink size={15} />
                      </a>
                      <Link to={`/admin/posts/${p.slug}/edit`} title="Edit"
                            data-testid={`admin-edit-${p.slug}`}
                            className="p-2 text-stone-600 hover:text-[#9c4122] hover:bg-stone-100">
                        <Pencil size={15} />
                      </Link>
                      <button onClick={() => onDelete(p.slug, p.title)} title="Delete"
                              data-testid={`admin-delete-${p.slug}`}
                              className="p-2 text-stone-600 hover:text-red-600 hover:bg-stone-100">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
